import { type Config } from "tailwindcss"
import tailwindAnimate from "tailwindcss-animate"
import defaultTheme from "tailwindcss/defaultTheme"
import plugin from "tailwindcss/plugin"

const getColorString = (colorVariable: string, opacityVariable?: string) =>
  opacityVariable
    ? `hsl(${colorVariable} / var(${opacityVariable}, 1))`
    : `hsl(${colorVariable})`

const colors = (opacityVariable?: string) => ({
  border: getColorString("var(--border)", opacityVariable),
  input: getColorString("var(--input)", opacityVariable),
  ring: getColorString("var(--ring)", opacityVariable),
  background: getColorString("var(--secondary-700)", opacityVariable),
  foreground: getColorString("var(--foreground)", opacityVariable),
  dialogBackground: "hsla(var(--dialog-background))",
  skeleton: {
    light: getColorString("var(--skeleton-light)", opacityVariable),
    medium: getColorString("var(--skeleton-medium)", opacityVariable),
    primary: getColorString("var(--skeleton-primary)", opacityVariable),
  },
  primary: {
    DEFAULT: getColorString("var(--primary-300)", opacityVariable),
    100: getColorString("var(--primary-100)", opacityVariable),
    200: getColorString("var(--primary-200)", opacityVariable),
    300: getColorString("var(--primary-300)", opacityVariable),
    400: getColorString("var(--primary-400)", opacityVariable),
    500: getColorString("var(--primary-500)", opacityVariable),
    600: getColorString("var(--primary-600)", opacityVariable),
    700: getColorString("var(--primary-700)", opacityVariable),
  },
  secondary: {
    DEFAULT: getColorString("var(--secondary-300)", opacityVariable),
    100: getColorString("var(--secondary-100)", opacityVariable),
    200: getColorString("var(--secondary-200)", opacityVariable),
    300: getColorString("var(--secondary-300)", opacityVariable),
    400: getColorString("var(--secondary-400)", opacityVariable),
    500: getColorString("var(--secondary-500)", opacityVariable),
    600: getColorString("var(--secondary-600)", opacityVariable),
    700: getColorString("var(--secondary-700)", opacityVariable),
  },
  green: {
    DEFAULT: getColorString("var(--accent-green-300)", opacityVariable),
    100: getColorString("var(--accent-green-100)", opacityVariable),
    200: getColorString("var(--accent-green-200)", opacityVariable),
    300: getColorString("var(--accent-green-300)", opacityVariable),
    400: getColorString("var(--accent-green-400)", opacityVariable),
    500: getColorString("var(--accent-green-500)", opacityVariable),
    600: getColorString("var(--accent-green-600)", opacityVariable),
  },
  blue: {
    DEFAULT: getColorString("var(--accent-blue-300)", opacityVariable),
    100: getColorString("var(--accent-blue-100)", opacityVariable),
    200: getColorString("var(--accent-blue-200)", opacityVariable),
    300: getColorString("var(--accent-blue-300)", opacityVariable),
    400: getColorString("var(--accent-blue-400)", opacityVariable),
    500: getColorString("var(--accent-blue-500)", opacityVariable),
    600: getColorString("var(--accent-blue-600)", opacityVariable),
  },
  yellow: {
    DEFAULT: getColorString("var(--accent-yellow-300)", opacityVariable),
    100: getColorString("var(--accent-yellow-100)", opacityVariable),
    200: getColorString("var(--accent-yellow-200)", opacityVariable),
    300: getColorString("var(--accent-yellow-300)", opacityVariable),
    400: getColorString("var(--accent-yellow-400)", opacityVariable),
    500: getColorString("var(--accent-yellow-500)", opacityVariable),
    600: getColorString("var(--accent-yellow-600)", opacityVariable),
  },
  red: {
    DEFAULT: getColorString("var(--accent-red-300)", opacityVariable),
    100: getColorString("var(--accent-red-100)", opacityVariable),
    200: getColorString("var(--accent-red-200)", opacityVariable),
    300: getColorString("var(--accent-red-300)", opacityVariable),
    400: getColorString("var(--accent-red-400)", opacityVariable),
    500: getColorString("var(--accent-red-500)", opacityVariable),
    600: getColorString("var(--accent-red-600)", opacityVariable),
  },
  destructive: {
    DEFAULT: getColorString("var(--accent-red-400)", opacityVariable),
    foreground: getColorString("var(--accent-red-100)", opacityVariable),
  },
  info: {
    DEFAULT: getColorString("var(--accent-blue-400)", opacityVariable),
    foreground: getColorString("var(--accent-blue-100)", opacityVariable),
  },
  warning: {
    DEFAULT: getColorString("var(--warning)", opacityVariable),
    foreground: getColorString("var(--warning-foreground)", opacityVariable),
  },
  utility: {
    DEFAULT: getColorString("var(--utility)", opacityVariable),
    foreground: getColorString("var(--utility-foreground)", opacityVariable),
  },
  muted: {
    DEFAULT: getColorString("var(--muted)", opacityVariable),
    foreground: getColorString("var(--muted-foreground)", opacityVariable),
  },
  accent: {
    DEFAULT: getColorString("var(--accent)", opacityVariable),
    foreground: getColorString("var(--accent-foreground)", opacityVariable),
  },
  popover: {
    DEFAULT: getColorString("var(--popover)", opacityVariable),
    foreground: getColorString("var(--popover-foreground)", opacityVariable),
  },
  icon: {
    primary: getColorString("var(--primary-500)", opacityVariable),
    secondary: getColorString("var(--secondary-400)", opacityVariable),
  },
  // So Energy brand colors
  "so-lime": "#CBFB00",
  "so-green": "#B6E100",
  "so-black": "#14140A",
  "so-bg": "#FAF6F3",
  "so-dark": "#38413D",
  "so-gray": "#E8E7E1",
  card: {
    DEFAULT: getColorString("var(--card)", opacityVariable),
    foreground: getColorString("var(--card-foreground)", opacityVariable),
  },
})

export default {
  darkMode: ["class"],
  safelist: ["dark"],
  prefix: "",
  future: {
    relativeContentPathsByDefault: true,
  },
  content: ["./src/**/*.{ts,tsx,vue,mdx,jsx}"],

  theme: {
    screens: {
      sm: "640px",
      md: "1024px",
      lg: "1440px",
      xl: "1920px",
    },
    container: {
      center: true,
      screens: {
        xl: "1187px",
      },
    },
    fontFamily: {
      sans: ["var(--font-founders-grotesk)", ...defaultTheme.fontFamily.sans],
      button: ["Arial, sans-serif"],
      black: ["Arial Black", "Arial", "sans-serif"],
    },
    extend: {
      boxShadow: {
        DEFAULT: "0px 8px 16px 0px hsla(var(--shadow))",
        sm: "0px 4px 8px 0px hsla(var(--shadow))",
        md: "0px 8px 16px 0px hsla(var(--shadow))",
        lg: "0px 12px 24px 0px hsla(var(--shadow))",
        switch:
          "rgba(0, 0, 0, 0.3) 0px 0px 1px, rgba(0, 0, 0, 0.2) 0px 1px 2px",
      },
      height: {
        4.5: "1.125rem",
        13: "3.25rem",
      },
      width: {
        4.5: "1.125rem",
      },
      spacing: {
        17: "4.25rem",
        18: "4.5rem",
      },
      backgroundColor: colors("--tw-bg-opacity"),
      borderColor: colors("--tw-border-opacity"),
      textColor: colors("--tw-empty"),
      colors: colors(),
      borderRadius: {
        DEFAULT: "12px",
        xs: "4px",
        sm: "8px",
        md: "16px",
        full: "360px",
      },
      fontSize: {
        xs: ["0.875rem", "1.25rem"],
        sm: ["1rem", "1.5rem"],
        base: ["1.125rem", "1.75rem"],
        lg: ["1.25rem", "1.75rem"],
        xl: [
          "1.5rem",
          {
            lineHeight: "2rem",
            letterSpacing: "-0.00375rem",
          },
        ],
        "2xl": [
          "1.75rem",
          {
            lineHeight: "2rem",
            letterSpacing: "-0.0175rem",
          },
        ],
        "3xl": ["2rem", { lineHeight: "2.25rem", letterSpacing: "-0.04rem" }],
        "4xl": [
          "2.5rem",
          {
            lineHeight: "2.5rem",
            letterSpacing: "-0.025rem",
          },
        ],
      },
      keyframes: {
        "accordion-down": {
          from: { height: "0" },
          to: { height: "var(--reka-accordion-content-height)" },
        },
        "accordion-up": {
          from: { height: "var(--reka-accordion-content-height)" },
          to: { height: "0" },
        },
        "collapsible-down": {
          from: { height: "0" },
          to: { height: "var(--reka-collapsible-content-height)" },
        },
        "collapsible-up": {
          from: { height: "var(--reka-collapsible-content-height)" },
          to: { height: "0" },
        },
        // 24 is just over the length of the lucide Check path, so the dash
        // covers the whole tick.
        "tick-draw": {
          from: { strokeDasharray: "24", strokeDashoffset: "24" },
          to: { strokeDasharray: "24", strokeDashoffset: "0" },
        },
        ripple: {
          from: { transform: "scale(1)", opacity: "0.6" },
          to: { transform: "scale(2)", opacity: "0" },
        },
      },
      animation: {
        "accordion-down": "accordion-down 0.2s ease-out",
        "accordion-up": "accordion-up 0.2s ease-out",
        "collapsible-down": "collapsible-down 0.2s ease-in-out",
        "collapsible-up": "collapsible-up 0.2s ease-in-out",
        "tick-draw": "tick-draw 0.4s ease-out 0.3s both",
        ripple: "ripple 0.6s ease-out 0.3s both",
      },
      maxWidth: {
        card: "745px",
      },
    },
  },
  plugins: [
    plugin(({ addVariant }) => {
      addVariant("has-hover", "@media (hover: hover) and (pointer: fine)")
      addVariant(
        "no-hover",
        "@media not all and (hover: hover) and (pointer: fine)",
      )
    }),
    tailwindAnimate,
  ],
} satisfies Config
